import { html, nothing } from 'lit';
import { erklaereLieferdatum } from '../../features/delivery-date/explain-delivery-date.js';
import type { MailExtractionResult } from '../../features/information-extraction/db/MailExtractionResult.js';

/**
 * „Wie kommt dieser Liefertermin zustande?" — der Abschnitt dazu unter der Extraktions-Timeline.
 *
 * Der Termin ist das einzige Feld im Angebot, das niemand aus der Anfrage ablesen kann: Er
 * entsteht aus Transportzone, Routenfahrplan, Annahmeschluss und Feiertagskalender. Ohne diese
 * Herleitung bleibt er eine Zahl, der man glauben muss — und im Zweifel überschreibt man sie.
 *
 * Die Sätze selbst stehen in `explain-delivery-date.ts` und sind dort geprüft; hier wird nur
 * gesetzt.
 */
export const DeliveryDateExplanation = (props: { extractionResult?: MailExtractionResult | null }) => {
  const result = props.extractionResult;
  const basis = result?.deliveryDateBasis;
  const geltend = result?.manualDeliveryDate ?? result?.plannedDeliveryDate;

  if (!result || !geltend)
    return html`
      <div class="text-sm text-base-content/40">
        Für diese Anfrage wurde noch kein Liefertermin berechnet. Beim nächsten SAP-Versand geschieht das nach, ohne
        dass hier etwas zu tun ist.
      </div>
    `;

  const schritte = erklaereLieferdatum(basis, geltend, result.manualDeliveryDate ? result.plannedDeliveryDate : null);
  if (!schritte.length)
    return html`<div class="text-sm text-base-content/40">Zu diesem Termin liegt keine Herleitung vor.</div>`;

  const letzter = schritte.length - 1;

  return html`
    <ol class="relative flex flex-col gap-1">
      ${schritte.map((schritt, i) => {
        const istErgebnis = i === letzter;
        return html`
          <li class="flex gap-3">
            <div class="flex flex-col items-center">
              <div class="flex items-center justify-center w-6 h-6 shrink-0">
                <x-icon class="${istErgebnis ? 'text-success' : 'text-base-content/30'} text-base">
                  ${istErgebnis ? 'event_available' : 'radio_button_unchecked'}
                </x-icon>
              </div>
              ${istErgebnis ? nothing : html`<div class="w-px flex-1 my-0.5 bg-base-300"></div>`}
            </div>
            <div class="flex-1 min-w-0 pb-3">
              <div class="text-[10.5px] uppercase tracking-wider font-semibold text-base-content/40">
                ${schritt.titel}
              </div>
              <div class="text-sm ${istErgebnis ? 'font-bold' : 'font-medium'} text-base-content">${schritt.wert}</div>
              ${schritt.hinweise.map(
                hinweis => html`<div class="text-[12.5px] text-base-content/55 mt-0.5">${hinweis}</div>`,
              )}
            </div>
          </li>
        `;
      })}
    </ol>
  `;
};
